import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Table as TableIcon } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { Banner } from '@/components/Banner'
import { STALE_DRAFT_SAVE_REASON, StaleDraftBanner } from '@/components/StaleDraftBanner'
import { CheckboxDropdown } from '@/components/CheckboxDropdown'
import { ChipInput } from '@/components/ChipInput'
import { ConfirmModal } from '@/components/ConfirmModal'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Tooltip } from '@/components/Tooltip'
import { useDismissedBanner } from '@/hooks/useDismissedBanner'
import { useServerSeed } from '@/hooks/useServerSeed'
import { errorMessage, extractFieldErrors, formatSaveError } from '@/lib/apiErrors'
import { deriveLookupTypeOptions } from '@/lib/lookupTypes'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { LookupEntry, LookupTableFile } from '@/types/bundle'
import {
  createBlankEntry,
  createLocalKey,
  duplicateEntry,
  isValidDomainShape,
  parseCollisionPath,
} from './lookupUtils'
import { LookupEntryForm } from './components/LookupEntryRow'
import { getLookupTableColumns, type LookupTableRow } from './components/lookupTableColumns'

function SkeletonBlock() {
  return <div className="h-48 animate-pulse rounded-control bg-surface-raised" />
}

const BANNER_STORAGE_KEY = 'legalflow-config-lookup-table-banner-dismissed'

function rowsToEntries(rows: LookupTableRow[]): LookupEntry[] {
  return rows.map((row) => row.entry)
}

function rowsFromEntries(entries: LookupEntry[]): LookupTableRow[] {
  return entries.map((entry) => ({ localKey: createLocalKey(), entry }))
}

const QUERY_KEY = ['draft-file', 'lookup_table']

// The file the editable rows describe, for saving and the dirty check.
function fileFromRows(rows: LookupTableRow[], stripTokens: string[], snapshot: LookupTableFile): LookupTableFile {
  return { schema_version: snapshot.schema_version, strip_tokens: stripTokens, entries: rowsToEntries(rows) }
}

export function LookupTableScreen() {
  const queryClient = useQueryClient()
  const lookupQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => getDraftFile<LookupTableFile>('lookup_table'),
  })

  const [rows, setRows] = useState<LookupTableRow[] | null>(null)
  const [stripTokens, setStripTokens] = useState<string[] | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<LookupTableFile | null>(null)
  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<LookupTableRow | null>(null)
  // Keys created via Add Entry/Duplicate this session - plays the row's
  // entrance animation once, same pattern as document-types' FieldsTab
  // bornKeys.
  const [bornKeys, setBornKeys] = useState<Set<string>>(new Set())
  const [bannerDismissed, dismissBanner] = useDismissedBanner(BANNER_STORAGE_KEY)

  // Seeds the editable rows, with a fresh client-only localKey per entry,
  // and follows server changes (hooks/useServerSeed.ts) - so an entry
  // created and then discarded in Diff & Validation does not linger here.
  const serverSeed = useServerSeed(QUERY_KEY, lookupQuery, {
    seed: (data) => {
      setSavedSnapshot(data)
      setRows(rowsFromEntries(data.entries))
      setStripTokens(data.strip_tokens ?? [])
    },
    isDirty: () =>
      rows !== null &&
      stripTokens !== null &&
      savedSnapshot !== null &&
      JSON.stringify(fileFromRows(rows, stripTokens, savedSnapshot)) !== JSON.stringify(savedSnapshot),
  })

  const saveMutation = useMutation({
    mutationFn: (payload: LookupTableFile) => putDraftFile<LookupTableFile>('lookup_table', payload),
    onSuccess: (_result, payload) => {
      setSavedSnapshot(payload)
      setRows(rowsFromEntries(payload.entries))
      setStripTokens(payload.strip_tokens ?? [])
      serverSeed.noteSaved(payload)
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'lookup_table' })
    },
  })

  if (lookupQuery.isPending) {
    return <SkeletonBlock />
  }

  if (lookupQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load lookup table">
        {errorMessage(lookupQuery.error)}
      </Banner>
    )
  }

  if (!rows || !savedSnapshot || stripTokens === null) {
    return <SkeletonBlock />
  }

  function updateRow(localKey: string, updater: (entry: LookupEntry) => LookupEntry) {
    setRows((current) =>
      (current ?? []).map((row) => (row.localKey === localKey ? { ...row, entry: updater(row.entry) } : row)),
    )
  }

  function handleAddEntry() {
    const localKey = createLocalKey()
    setRows((current) => [...(current ?? []), { localKey, entry: createBlankEntry() }])
    setExpandedKey(localKey)
    setBornKeys((current) => new Set(current).add(localKey))
  }

  function handleDuplicate(source: LookupTableRow) {
    const localKey = createLocalKey()
    setRows((current) => [...(current ?? []), { localKey, entry: duplicateEntry(source.entry) }])
    setExpandedKey(localKey)
    setBornKeys((current) => new Set(current).add(localKey))
  }

  function toggleExpand(localKey: string) {
    setExpandedKey((current) => (current === localKey ? null : localKey))
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return
    setRows((current) => (current ?? []).filter((row) => row.localKey !== deleteTarget.localKey))
    setExpandedKey((current) => (current === deleteTarget.localKey ? null : current))
    setDeleteTarget(null)
  }

  async function handleSave() {
    if (hasEmptyName || hasInvalidDomain || !(await serverSeed.ensureCurrent())) return
    saveMutation.mutate(currentFile)
  }

  const currentFile = fileFromRows(rows, stripTokens, savedSnapshot)
  const isDirty = JSON.stringify(currentFile) !== JSON.stringify(savedSnapshot)
  const hasEmptyName = rows.some((row) => row.entry.name.trim().length === 0)
  const hasInvalidDomain = rows.some((row) => {
    const domain = row.entry.domain?.trim() ?? ''
    return domain.length > 0 && !isValidDomainShape(domain)
  })

  // Routes PRD 22's collision error (entries[type=X,name=Y]) back to the
  // specific offending row - never a client-side guess at duplication, only
  // reads the server's own answer (AC3/AC5).
  const fieldErrors = extractFieldErrors(saveMutation.error)
  const rowServerErrors: Record<string, string> = {}
  let collisionExpandKey: string | null = null
  fieldErrors?.forEach((err) => {
    const collision = parseCollisionPath(err.path)
    if (!collision) return
    const match = rows.find((row) => row.entry.type === collision.type && row.entry.name === collision.name)
    if (match) {
      rowServerErrors[match.localKey] = err.message
      collisionExpandKey = match.localKey
    }
  })
  if (collisionExpandKey && collisionExpandKey !== expandedKey) {
    setExpandedKey(collisionExpandKey)
  }

  const typeOptions = deriveLookupTypeOptions(rows.map((row) => row.entry))

  const filteredRows = rows.filter((row) => {
    if (typeFilter.length > 0 && !typeFilter.includes(row.entry.type)) return false
    const query = search.trim().toLowerCase()
    if (!query) return true
    const nameMatches = row.entry.name.toLowerCase().includes(query)
    const aliasMatches = (row.entry.aliases ?? []).some((alias) => alias.toLowerCase().includes(query))
    return nameMatches || aliasMatches
  })

  const columns = getLookupTableColumns({
    expandedKey,
    onToggleExpand: toggleExpand,
    onDuplicate: handleDuplicate,
    onDelete: setDeleteTarget,
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-title font-semibold text-text-primary">Lookup Table</h1>
        <button
          type="button"
          onClick={handleAddEntry}
          className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
        >
          + Add Entry
        </button>
      </div>

      {!bannerDismissed && (
        <Banner variant="info" icon={TableIcon} title="This table is not fuzzy-matched" onDismiss={dismissBanner}>
          Add exact aliases for name variants you see in real documents
        </Banner>
      )}

      {serverSeed.baseChanged && <StaleDraftBanner onReload={serverSeed.reload} />}

      {saveMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Save failed">
          {formatSaveError(saveMutation.error, fieldErrors)}
        </Banner>
      )}

      <section className="flex flex-col gap-2 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Strip tokens
          <InfoTooltip text={'Trailing suffix words the Lookup Table Engine strips from raw extracted candidates before matching (e.g. "Stoneacre Properties Ltd" → "stoneacre") - never applied to curated entry names/aliases themselves.'} />
        </h2>
        <ChipInput value={stripTokens} onChange={setStripTokens} placeholder="Type a word, press Enter…" />
      </section>

      <div className="flex items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or alias…"
          className="w-80 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
        <CheckboxDropdown
          values={typeFilter}
          onChange={setTypeFilter}
          options={typeOptions}
          placeholder="All types"
          className="w-48"
        />
      </div>

      {filteredRows.length === 0 ? (
        <EmptyState
          icon={TableIcon}
          headline={rows.length === 0 ? 'No entries added yet' : 'No entries match your filters'}
          description="Add a lookup-table entry to start resolving noisy extracted names to a canonical entry."
          action={{ label: '+ Add Entry', onClick: handleAddEntry }}
        />
      ) : (
        <DataTable
          columns={columns}
          data={filteredRows}
          getRowKey={(row) => row.localKey}
          expandedRowKey={expandedKey ?? undefined}
          renderExpanded={(row) => (
            <LookupEntryForm
              entry={row.entry}
              onChange={(entry) => updateRow(row.localKey, () => entry)}
              typeOptions={typeOptions}
              serverError={rowServerErrors[row.localKey]}
              isNew={bornKeys.has(row.localKey)}
            />
          )}
        />
      )}

      <div className="flex justify-end">
        {(() => {
          const disabledReason = !isDirty
            ? 'No changes to save'
            : serverSeed.baseChanged
              ? STALE_DRAFT_SAVE_REASON
              : hasEmptyName
              ? 'Every entry needs a name before saving'
              : hasInvalidDomain
                ? "Fix the domain that doesn't look valid before saving"
                : null

          if (disabledReason && !saveMutation.isPending) {
            return (
              <Tooltip text={disabledReason}>
                <button
                  type="button"
                  aria-disabled="true"
                  className="cursor-not-allowed rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  Save
                </button>
              </Tooltip>
            )
          }

          return (
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={handleSave}
              className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )
        })()}
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete entry"
        description={`"${deleteTarget?.entry.name.trim() || 'Untitled entry'}" will be removed from the draft, including its type, aliases, and domain. It is only persisted on your next Save.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
